import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  CheckCircleIcon,
  PlugsConnectedIcon,
  PlugsIcon,
  WarningIcon,
  XCircleIcon,
} from '@phosphor-icons/react';
import {
  fetchIngestEvents,
  fetchIntegrationsHealth,
  fetchRcUnmapped,
} from '@/features/integrations/api';
import { Button, Card, CardContent, CardHeader, CardTitle, PageHeader, StatusChip, fmtRelativeTime, type Tone } from '@smena/ui';
import { AvitoCard } from '@/features/avito/AvitoCard';
import { GuestChannelsCard } from '@/features/channels/GuestChannelsCard';
import { BookingsImportCard } from '@/features/integrations/BookingsImportCard';
import { IcalFeedsCard } from '@/features/integrations/IcalFeedsCard';
import { MaxPersonalCard } from '@/features/max-personal/MaxPersonalCard';
import { OperatorNotifyCard } from '@/features/ops-notify/OperatorNotifyCard';
import { RealtyCalendarCard } from '@/features/integrations/RealtyCalendarCard';
import { TgPersonalCard } from '@/features/tg-personal/TgPersonalCard';

const ACTION_TONE: Record<string, Tone> = {
  created: 'green',
  updated: 'accent',
  cancelled: 'muted',
  error: 'red',
  skipped: 'amber',
};

/** Исходы, при которых бронь не появилась. Их и показывает срез «не доехало». */
const FAILED_ACTIONS = new Set(['error', 'skipped']);

export function IntegrationsPage() {
  const eventsQ = useQuery({ queryKey: ['ingest-events'], queryFn: fetchIngestEvents, refetchInterval: 10_000 });
  const unmappedQ = useQuery({ queryKey: ['rc-unmapped'], queryFn: fetchRcUnmapped, refetchInterval: 15_000 });
  // Лента смешивает удачные и неудачные события, а ищут в ней всегда вторые: вопрос к ней
  // возникает, когда брони нет. По умолчанию показываем всё - срез включается кнопкой.
  const [onlyFailed, setOnlyFailed] = useState(false);
  const allEvents = eventsQ.data ?? [];
  const failedCount = allEvents.filter((e) => FAILED_ACTIONS.has(e.action)).length;
  const events = onlyFailed ? allEvents.filter((e) => FAILED_ACTIONS.has(e.action)) : allEvents;

  return (
    <div className="flex flex-col gap-4">
      <div>
        <PageHeader
          icon={<PlugsIcon weight="bold" />}
          title="Интеграции"
          description="Откуда приходят брони и где Smena разговаривает с гостями."
        />
      </div>

      {/* Каналы гостя: состояние аккаунта плюс готовность стенда, с отвязкой.
          Раньше здесь были read-only чипы из env - управлять каналом было неоткуда. */}
      <GuestChannelsCard />

      {/* Оповещения оператора - сразу под каналами: без них эскалация никуда не уходит,
          и это важнее любой интеграции ниже. */}
      <OperatorNotifyCard />

      {/* Каналы по личным аккаунтам УК: у каждого свой жизненный цикл (QR/2FA/прогрев/разрыв),
          поэтому они живут отдельными карточками, а не строками в списке каналов выше. */}
      <MaxPersonalCard />
      <TgPersonalCard />

      {/* Источник броней №1: своя карточка - клиент забирает отсюда ссылку для кабинета RC. */}
      <RealtyCalendarCard />

      {/* Avito - второй источник броней после RC, и его тоже надо уметь отключать. */}
      <AvitoCard />

      {/* iCal: раньше здесь был счётчик без списка, поэтому удалить фид было неоткуда. */}
      <IcalFeedsCard />

      {/* Импорт из файла: ручки upload/template были с самого начала, а интерфейса к ним - нет.
          Мастер настройки при этом отправлял за шаблоном именно сюда. */}
      <BookingsImportCard />

      {/* Парсер объявлений - справка, отключать нечего. */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Парсер объявлений</CardTitle>
          </CardHeader>
          <CardContent className="text-xs text-muted">
            Суточно, Яндекс Путешествия, Авито.
            <br />
            По ссылке на объявление Smena сама заполнит карточку квартиры и базу знаний.
          </CardContent>
        </Card>
      </div>

      {/* Unmapped RC-объекты */}
      {(unmappedQ.data?.length ?? 0) > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <WarningIcon weight="fill" className="size-4 text-amber" />
              RC: неизвестные объекты ({unmappedQ.data?.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wider text-muted">
                <tr>
                  <th className="pb-2">rc_object_id</th>
                  <th className="pb-2">Подсказка</th>
                  <th className="pb-2">Событий</th>
                  <th className="pb-2">Последнее</th>
                </tr>
              </thead>
              <tbody>
                {unmappedQ.data!.map((u) => (
                  <tr key={u.id} className="border-t border-line/40">
                    <td className="py-2 font-mono text-xs">{u.rcObjectId}</td>
                    <td className="py-2">{u.nameHint ?? '-'}</td>
                    <td className="py-2 tabular-nums">{u.eventsCount}</td>
                    <td className="py-2 text-xs text-muted">{fmtRelativeTime(u.lastEventAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      ) : null}

      {/* Что приходило от источников */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-3">
          <CardTitle>Что приходило от источников</CardTitle>
          {failedCount > 0 ? (
            <div className="flex gap-1">
              <Button
                size="sm"
                variant={onlyFailed ? 'ghost' : 'secondary'}
                onClick={() => setOnlyFailed(false)}
              >
                Все
              </Button>
              <Button
                size="sm"
                variant={onlyFailed ? 'secondary' : 'ghost'}
                onClick={() => setOnlyFailed(true)}
              >
                Не доехало ({failedCount})
              </Button>
            </div>
          ) : null}
        </CardHeader>
        <CardContent>
          {eventsQ.isLoading ? (
            <div className="text-center text-sm text-muted">Загрузка…</div>
          ) : events.length === 0 ? (
            <div className="text-center text-sm text-muted">
              {onlyFailed ? 'Все события доехали.' : 'Пока ничего не приходило.'}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wider text-muted">
                <tr>
                  <th className="pb-2">Когда</th>
                  <th className="pb-2">Источник</th>
                  <th className="pb-2">Действие</th>
                  <th className="pb-2">Детали</th>
                </tr>
              </thead>
              <tbody>
                {events.map((e) => (
                  <tr key={e.id} className="border-t border-line/40">
                    <td className="py-2 text-xs text-muted">{fmtRelativeTime(e.ts)}</td>
                    <td className="py-2 font-mono text-xs">{e.source}</td>
                    <td className="py-2">
                      <StatusChip tone={ACTION_TONE[e.action] ?? 'muted'}>{e.action}</StatusChip>
                    </td>
                    <td className="py-2 truncate text-xs text-muted">
                      {Object.entries(e.detail)
                        .map(([k, v]) => `${k}: ${String(v)}`)
                        .join(', ') || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
